import React, { useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from 'recharts';
import { Shield, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import type { SDQReport } from '../../../types';
import ScoreRing from './ScoreRing';
import DimensionBar from './DimensionBar';
import { DIMENSION_SHORT_LABELS, sortDimensionsByScore, scoreColor } from './dashboard-utils';

// recharts type compat with React 19
const _RadarChart = RadarChart as any;
const _Radar = Radar as any;
const _PolarGrid = PolarGrid as any;
const _PolarAngleAxis = PolarAngleAxis as any;
const _PolarRadiusAxis = PolarRadiusAxis as any;
const _ResponsiveContainer = ResponsiveContainer as any;

interface ExecutiveTabProps {
  report: SDQReport;
}

export default function ExecutiveTab({ report }: ExecutiveTabProps) {
  const { composite, dimensions } = report;
  const sorted = sortDimensionsByScore(dimensions);

  // Radar data
  const radarData = Object.entries(dimensions)
    .filter(([, d]) => d.enabled)
    .map(([name, d]) => ({
      dimension: DIMENSION_SHORT_LABELS[name] || name,
      score: d.score,
      fullMark: 100,
    }));

  const aiSafe = composite.ai_safe;

  return (
    <div className="space-y-6">
      {/* Top row: DQS ring + AI safety + stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* DQS Ring */}
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6 flex flex-col items-center justify-center">
          <ScoreRing
            score={composite.dqs}
            grade={composite.grade}
            label="Data Quality Score"
          />
          <p className="mt-3 text-sm font-medium text-cargo-muted">{composite.label}</p>
          <p className="text-xs text-cargo-muted mt-1">Action: {composite.action.replace(/_/g, ' ')}</p>
        </div>

        {/* AI Safety */}
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6 flex flex-col items-center justify-center">
          {aiSafe ? (
            <>
              <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mb-3">
                <Shield className="w-8 h-8 text-cargo-success" />
              </div>
              <p className="text-lg font-bold text-cargo-success">AI Safe</p>
              <p className="text-sm text-cargo-muted mt-1">Data meets AI readiness threshold</p>
            </>
          ) : (
            <SdqAiSafetyBlock report={report} />
          )}
        </div>

        {/* Issue summary */}
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
          <h3 className="text-sm font-semibold text-cargo-muted mb-4">Issues Summary</h3>
          <div className="space-y-3">
            {[
              { label: 'Critical', count: report.issues_summary.critical, color: 'bg-red-500' },
              { label: 'High', count: report.issues_summary.high, color: 'bg-orange-500' },
              { label: 'Medium', count: report.issues_summary.medium, color: 'bg-amber-500' },
              { label: 'Low', count: report.issues_summary.low, color: 'bg-blue-500' },
            ].map(({ label, count, color }) => (
              <div key={label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
                  <span className="text-sm text-cargo-text">{label}</span>
                </div>
                <span className="text-sm font-semibold text-cargo-text">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dimension bars (sorted worst-first) */}
      <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
        <h3 className="text-sm font-semibold text-cargo-muted mb-4">Dimension Scores</h3>
        <div className="space-y-1">
          {sorted.map(([name, dim]) => (
            <DimensionBar key={name} name={name} dim={dim} />
          ))}
        </div>
      </div>

      {/* Radar Chart */}
      <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
        <h3 className="text-sm font-semibold text-cargo-muted mb-4">Quality Radar</h3>
        <div className="w-full" style={{ height: 350 }}>
          <_ResponsiveContainer width="100%" height="100%">
            <_RadarChart data={radarData} outerRadius="75%">
              <_PolarGrid stroke="#d1d9e6" />
              <_PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11, fill: '#64748b' }} />
              <_PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} />
              <_Radar
                name="Score"
                dataKey="score"
                stroke={scoreColor(composite.dqs)}
                fill={scoreColor(composite.dqs)}
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </_RadarChart>
          </_ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function SdqAiSafetyBlock({ report }: { report: SDQReport }) {
  const [open, setOpen] = useState(false);
  const { composite, dimensions } = report;

  const reasons: string[] = [];
  if (composite.dqs < composite.threshold) {
    reasons.push(`Score ${composite.dqs.toFixed(1)} is below threshold (${composite.threshold})`);
  }
  const lowDims = Object.entries(dimensions).filter(([, d]) => d.enabled && d.score < 40);
  if (lowDims.length > 0) {
    const names = lowDims.map(([k, d]) =>
      `${DIMENSION_SHORT_LABELS[k] || k} (${d.score.toFixed(0)})`
    ).join(', ');
    reasons.push(`Dimension below min (40): ${names}`);
  }
  const critDims: string[] = [];
  for (const [k, d] of Object.entries(dimensions)) {
    const critCount = d.issues.filter((i: any) => i.severity === 'critical').length;
    if (critCount > 0) {
      critDims.push(`${DIMENSION_SHORT_LABELS[k] || k} (${critCount})`);
    }
  }
  if (critDims.length > 0) {
    reasons.push(`Critical issues in: ${critDims.join(', ')}`);
  }
  if (reasons.length === 0) {
    reasons.push('AI safety conditions not met');
  }

  return (
    <>
      <div className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center mb-3">
        <ShieldAlert className="w-8 h-8 text-cargo-error" />
      </div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-lg font-bold text-cargo-error hover:text-red-400 transition-colors cursor-pointer"
      >
        Not AI Safe
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="mt-2 text-left bg-red-500/5 border border-red-500/15 rounded-lg px-3 py-2.5 w-full">
          <p className="text-[10px] font-semibold text-red-400 mb-1.5 uppercase tracking-wide">Reason</p>
          {reasons.map((r, i) => (
            <p key={i} className="text-xs text-cargo-muted mt-0.5 flex items-start gap-1.5">
              <span className="text-cargo-error shrink-0">&#x2022;</span>
              {r}
            </p>
          ))}
        </div>
      )}
    </>
  );
}
